/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useState } from 'react'
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'

import {
  PageTitle,
  FollowersContainer,
  ProfileHeader,
  ProfileImage,
  ProfileInfo,
  ProfileName,
  ProfileSubtext,
  MainContent,
  LeftSection,
  RightSection,
  TagsContainer,
  Tag,
  StatsSection,
  StatColumn,
  StatValue,
  StatLabel,
  SummarySection,
  SummaryTitle,
  SummaryText,
  TagText,
  TagCount,
  CategoryTags,
  CategoryTag,
} from './Style'

import { LOCAL, DEVELOP } from 'constants/env'

interface TagType {
  name: string
  count: number
}

interface FollowerType {
  username: string
  platform: string
  bio: string
  profileImage: string
  tags: TagType[]
  profileSummary: string
  commentsSummary: string
  stats: {
    followers: string
    posts: string
    following: string
    likes: number
    comments: number
  }
}

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

async function socialProxy(apiPath: string, params: Record<string, string>): Promise<any> {
  const query = new URLSearchParams({ path: apiPath, ...params })
  const res = await fetch(`${config.API_URL}/api/social-proxy?${query.toString()}`)
  const json = await res.json()
  if (!res.ok || !json.success) throw new Error(json.error ?? 'Social API request failed')
  return json.data
}

const FollowerCard = React.memo(function FollowerCard({
  follower,
}: {
  follower: FollowerType
}) {
  return (
    <FollowersContainer sx={{ marginBottom: '44px' }}>
      <MainContent>
        <LeftSection>
          <ProfileHeader>
            <ProfileImage src={follower.profileImage} alt="Profile" />
            <ProfileInfo>
              <ProfileName>{follower.username}</ProfileName>
              <ProfileSubtext>{follower.platform}</ProfileSubtext>
              <ProfileSubtext>{follower.bio}</ProfileSubtext>
            </ProfileInfo>
          </ProfileHeader>
          <TagsContainer>
            {follower.tags.map((tag, tagIndex) => (
              <Tag key={tagIndex}>
                <TagText>{tag.name}</TagText>
                <TagCount>{tag.count}</TagCount>
              </Tag>
            ))}
          </TagsContainer>
        </LeftSection>
        <RightSection>
          <SummarySection>
            <SummaryTitle>Profile Summary</SummaryTitle>
            <SummaryText>{follower.profileSummary}</SummaryText>
          </SummarySection>
          <SummarySection>
            <SummaryTitle>Comments Summary</SummaryTitle>
            <SummaryText>{follower.commentsSummary}</SummaryText>
          </SummarySection>
          <hr
            style={{
              border: '1px solid #292929',
              marginTop: '23px',
              marginBottom: '30px',
            }}
          />
          <StatsSection>
            {Object.entries(follower.stats).map(([key, value]) => (
              <StatColumn key={key}>
                <StatLabel>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </StatLabel>
                <StatValue>{value}</StatValue>
              </StatColumn>
            ))}
          </StatsSection>
        </RightSection>
      </MainContent>
    </FollowersContainer>
  )
})

export default function FollowersList(): JSX.Element {
  const [username, setUsername] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [followersData, setFollowersData] = useState<FollowerType[]>([])
  const [followersListSimilarData, setFollowersListSimilarData] = useState<
    FollowerType[]
  >([])

  const generateProfileSummary = async (
    stats: {
      followers: string
      posts: string
      following: string
      likes: number
      comments: number
    },
    username: string
  ) => {
    const response = await fetch(`${config.API_URL}/api/generate-profile-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'profile', stats, username }),
    })
    const data = await response.json()
    return data.summary ?? ''
  }
  const generateCommentsSummary = async (
    stats: { comments: number },
    username: string
  ) => {
    const response = await fetch(`${config.API_URL}/api/generate-profile-summary`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'comments', stats, username }),
    })
    const data = await response.json()
    return data.summary ?? ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username) return
    setLoading(true)
    setError(null)
    try {
      const postIds = await fetchPostIds(username)
      if (!postIds.length) {
        setError('User has no posts.')
        return
      }
      const [allLikesData, allCommentsData] = await Promise.all([
        fetchLikesData(postIds),
        fetchCommentsData(postIds),
      ])
      const combinedPostData = combinePostData(
        postIds,
        allLikesData,
        allCommentsData
      )
      const topSimilarUser = await fetchSimilarUserInfo(username)

      const topUsers = getTopUsers(combinedPostData)

      const userInfoData = await fetchUserInfo(topUsers)
      const similarUserInfodata = await fetchUserInfo(topSimilarUser)

      const extractedData = await extractFollowerData(userInfoData, topUsers)

      const extractedSimilarData = await extractSimilarFollowerData(
        similarUserInfodata
      )

      setFollowersData(extractedData)
      setFollowersListSimilarData(extractedSimilarData)

      if (extractedData.length > 0) {
        await fetchProfileSummary(extractedData)
      }
      if (extractedSimilarData.length > 0) {
        await fetchProfileSimilarSummary(extractedSimilarData)
      }
    } catch (error) {
      console.error('Error fetching posts:', error)
      setError('Error fetching posts')
    } finally {
      setLoading(false)
    }
  }
  const fetchSimilarUserInfo = async (userName: string) => {
    try {
      const data = await socialProxy('/v1/similar_accounts', { username_or_id_or_url: userName })
      return data.items.slice(0, 3)
    } catch (error) {
      console.error(`Error fetching info for user ${userName}:`, error)
      return null
    }
  }
  const fetchPostIds = async (username: string): Promise<string[]> => {
    const data = await socialProxy('/v1/posts', { username_or_id_or_url: username })
    return data.items.map((post: any) => post.id)
  }
  const fetchLikesData = async (postIds: string[]) => {
    return await Promise.all(postIds.map(fetchLikes))
  }
  const fetchCommentsData = async (postIds: string[]) => {
    return await Promise.all(postIds.map(fetchComments))
  }
  const fetchLikes = async (postId: string) => {
    try {
      const data = await socialProxy('/v1/likes', { code_or_id_or_url: postId })
      return { postId, likes: data.items }
    } catch (error) {
      console.error(`Error fetching likes for post ${postId}:`, error)
      return { postId, likes: [] }
    }
  }
  const fetchComments = async (postId: string) => {
    try {
      const data = await socialProxy('/v1/comments', { code_or_id_or_url: postId })
      return { postId, comments: data.items }
    } catch (error) {
      console.error(`Error fetching comments for post ${postId}:`, error)
      return { postId, comments: [] }
    }
  }
  const combinePostData = (
    postIds: string[],
    allLikesData: any[],
    allCommentsData: any[]
  ) => {
    return postIds.map((postId: string) => {
      const likes =
        allLikesData.find((like) => like.postId === postId)?.likes || []
      const comments =
        allCommentsData.find((comment) => comment.postId === postId)
          ?.comments || []
      return { postId, likes, comments }
    })
  }
  const getTopUsers = (combinedPostData: any[]) => {
    const userCountMap: { [key: string]: any } = {}
    combinedPostData.forEach((post: any) => {
      post.likes.forEach((like: any) => {
        const username = like.username
        if (!userCountMap[username]) {
          userCountMap[username] = { count: 0, likeCount: 0, commentCount: 0 }
        }
        userCountMap[username].count++
        userCountMap[username].likeCount++
      })
      post.comments.forEach((comment: any) => {
        const username = comment.user.username
        if (!userCountMap[username]) {
          userCountMap[username] = { count: 0, likeCount: 0, commentCount: 0 }
        }
        userCountMap[username].count++
        userCountMap[username].commentCount++
      })
    })
    return Object.entries(userCountMap)
      .map(([username, { count, likeCount, commentCount }]) => ({
        username,
        count,
        likeCount,
        commentCount,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
  }
  const fetchUserInfo = async (topUsers: any[]) => {
    return await Promise.all(
      topUsers.map(async (user: any) => {
        try {
          return await socialProxy('/v1/info', {
            username_or_id_or_url: user.username,
            include_about: 'true',
            url_embed_safe: 'true',
          })
        } catch (error) {
          console.error(`Error fetching info for user ${user.username}:`, error)
          return null
        }
      })
    )
  }
  const extractFollowerData = async (userInfoData: any[], topUsers: any[]) => {
    return await Promise.all(
      userInfoData.map(async (info: any) => {
        const topUser = topUsers.find(
          (user) => user.username === info.about.username
        )
        const userPosts = await fetchUserPosts(info.about.username)
        const topHashtags = countHashtags(userPosts)
        return {
          username: info.about.username,
          platform: 'Instagram',
          bio: info.biography,
          profileImage: info.profile_pic_url,
          tags: topHashtags,
          profileSummary: '',
          commentsSummary: '',
          stats: {
            followers: info.follower_count,
            posts: info.media_count,
            following: info.following_count,
            likes: topUser?.likeCount ?? 0,
            comments: topUser?.commentCount ?? 0,
          },
          userPosts,
        }
      })
    )
  }
  const extractSimilarFollowerData = async (userInfoData: any[]) => {
    return await Promise.all(
      userInfoData.map(async (info: any) => {
        const userPosts = await fetchUserPosts(info.about.username)
        const topHashtags = countHashtags(userPosts)
        return {
          username: info.about.username,
          platform: 'Instagram',
          bio: info.biography,
          profileImage: info.profile_pic_url,
          tags: topHashtags,
          profileSummary: '',
          commentsSummary: '',
          stats: {
            followers: info.follower_count,
            posts: info.media_count,
            following: info.following_count,
            likes: 0,
            comments: 0,
          },
          userPosts,
        }
      })
    )
  }
  const fetchUserPosts = async (username: string) => {
    try {
      const data = await socialProxy('/v1/posts', { username_or_id_or_url: username })
      return data.items || []
    } catch (error) {
      console.error(`Error fetching posts for user ${username}:`, error)
      return []
    }
  }
  const countHashtags = (userPosts: any[]) => {
    const hashtagCount: { [key: string]: number } = {}
    userPosts.forEach((post) => {
      post.caption?.hashtags?.forEach((hashtag: string) => {
        hashtagCount[hashtag] = (hashtagCount[hashtag] || 0) + 1
      })
    })
    return Object.entries(hashtagCount)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, 5)
      .map(([hashtag, count]) => ({ name: hashtag, count }))
  }
  const fetchProfileSummary = async (data: FollowerType[]) => {
    try {
      const summaryPromises = data.map(async (follower) => {
        const profileSummary = await generateProfileSummary(
          follower.stats,
          follower.username
        )
        const commentsSummary = await generateCommentsSummary(
          follower.stats,
          follower.username
        )
        return { ...follower, profileSummary, commentsSummary }
      })
      const updatedFollowersData = await Promise.all(summaryPromises)
      setFollowersData(updatedFollowersData)
    } catch (error) {
      setError('Error fetching summaries')
    }
  }
  const fetchProfileSimilarSummary = async (data: FollowerType[]) => {
    try {
      const summaryPromises = data.map(async (follower) => {
        const profileSummary = await generateProfileSummary(
          follower.stats,
          follower.username
        )
        const commentsSummary = await generateCommentsSummary(
          follower.stats,
          follower.username
        )
        return { ...follower, profileSummary, commentsSummary }
      })
      const updatedFollowersData = await Promise.all(summaryPromises)
      setFollowersListSimilarData(updatedFollowersData)
    } catch (error) {
      setError('Error fetching summaries')
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: '#0d0d0d',
        padding: '44px',
        height: '100vh',
        overflow: 'auto',
      }}
    >
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <CategoryTags>
            <CategoryTag>#dogstuff</CategoryTag>
            <CategoryTag>#petlife</CategoryTag>
            <CategoryTag>#cutepups</CategoryTag>
            <CategoryTag>#animals</CategoryTag>
            <CategoryTag>#petderps</CategoryTag>
          </CategoryTags>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter Instagram handle"
              sx={{
                marginBottom: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1a1a1a',
                  '& input': {
                    color: '#ffffff',
                  },
                  '& fieldset': {
                    borderColor: '#333333',
                  },
                  '&:hover fieldset': {
                    borderColor: '#444444',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00c2e5',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#666666',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#666666',
                  opacity: 1,
                },
              }}
              InputProps={{
                sx: {
                  '&::placeholder': {
                    color: '#666666',
                    opacity: 1,
                  },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ marginBottom: 4 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Analyze Profile'}
            </Button>
          </form>
          {error && (
            <Typography color="error" sx={{ marginBottom: 2 }}>
              {error}
            </Typography>
          )}
          {followersListSimilarData?.length > 0 && (
            <PageTitle>Followers Similar To You</PageTitle>
          )}
          {followersListSimilarData.map((follower, index) => (
            <FollowerCard key={index} follower={follower} />
          ))}
          {followersListSimilarData?.length > 0 && (
            <PageTitle>Most Popular Followers</PageTitle>
          )}
          {followersData.map((follower, index) => (
            <FollowerCard key={index} follower={follower} />
          ))}
          {followersListSimilarData?.length > 0 && (
            <PageTitle>Most Active Followers</PageTitle>
          )}
          {followersData.map((follower, index) => (
            <FollowerCard key={index} follower={follower} />
          ))}
        </>
      )}
    </Box>
  )
}
